import {
  FC,
  FocusEventHandler,
  KeyboardEventHandler,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { InputNumber, InputNumberProps } from 'antd';
import { useFormContext } from 'react-hook-form';
import { extractRowIndexAndColumnName } from './tanstack/extractRowIndexAndColumnName';
import { useMoveFormTableRow } from './tanstack/useMoveFormTableRow';
import { useGetIdFromName } from '../utils';
import { useUpdateSequenceNumbersRef } from './tanstack/useUpdateSequenceNumbersRef';

export interface FormSequenceNumberInputProps
  extends Omit<InputNumberProps<number>, 'id'> {
  name: `${string}.${number}.${string}`;
  id?: string;
  onRangeChange?: (parentPath: string, start: number, end?: number) => void;
}

export const FormTableSequenceNumberInput: FC<FormSequenceNumberInputProps> = (
  props
) => {
  const getIdFromName = useGetIdFromName();
  const {
    name,
    id = getIdFromName(name),
    onBlur,
    onKeyDown,
    onRangeChange,
    ...rest
  } = props;
  const { fieldName, dataIndex, columnName } =
    extractRowIndexAndColumnName(name);

  if (!columnName) {
    throw new Error(
      `Expecting given name to include last dot-notated part as string but got "${columnName}" from name: "${name}"`
    );
  }

  const { getValues, setValue } = useFormContext();
  const [cachedValue, setCachedValue] = useState<number | null>(
    getValues(name)
  );

  const restoreValue = useCallback(
    () => setCachedValue(getValues(name)),
    [getValues, name]
  );

  const isDirty = useMemo(
    () => cachedValue !== getValues(name),
    [cachedValue, getValues, name]
  );

  const move = useMoveFormTableRow({ getValues, setValue }, columnName);
  const updateSequenceNumbers = useUpdateSequenceNumbersRef(
    fieldName,
    columnName
  );

  const confirmValue = useCallback(() => {
    if (!isDirty) {
      return;
    }

    if (cachedValue === null) {
      restoreValue();
      return;
    }

    const count = getValues(fieldName).length;
    const toIndex = Math.min(count - 1, Math.max(0, Number(cachedValue) - 1));

    // 1. move row data object in data array
    move(fieldName, dataIndex, toIndex);

    // 2. update sequence number values in row data objects (only those between from/to indexes)
    updateSequenceNumbers.current(
      Math.min(dataIndex, toIndex),
      Math.max(dataIndex, toIndex) + 1
    );

    onRangeChange?.(
      fieldName,
      Math.min(dataIndex, toIndex),
      Math.max(dataIndex, toIndex) + 1
    );

    // 3. update own cached value
    setCachedValue(toIndex + 1);
  }, [
    cachedValue,
    restoreValue,
    getValues,
    fieldName,
    move,
    dataIndex,
    isDirty,
    updateSequenceNumbers,
    onRangeChange,
  ]);

  const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      confirmValue();
      onBlur?.(event);
    },
    [confirmValue, onBlur]
  );

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        restoreValue();
      }
      if (event.key === 'Enter') {
        confirmValue();
      }
      onKeyDown?.(event);
    },
    [restoreValue, confirmValue, onKeyDown]
  );

  const handleChange = useCallback(
    (value: number | null) => {
      setCachedValue(value);
    },
    [setCachedValue]
  );

  return (
    <InputNumber
      id={id}
      {...rest}
      value={cachedValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
};
